import React, { useRef, useState, useEffect } from 'react'
import { Dialog } from '@mui/material'
import { useReactToPrint } from 'react-to-print'
import {
  StyledPrintContent,
  StyledDialogPaper,
  StyledPreviewContainer,
} from './Print.styled'
import PrintToolbar from './PrintToolbar'

interface PrintModalProps {
  open: boolean
  onClose: () => void
  children: React.ReactNode
  title?: string
}

const PrintModal = ({
  open,
  onClose,
  children,
  title = 'Print Preview',
}: PrintModalProps) => {
  const [options, setOptions] = useState({
    pageSize: 'a4',
    orientation: 'portrait',
  })

  const printRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (printRef.current) {
      const isLandscape = options.orientation === 'landscape'
      printRef.current.style.width = isLandscape ? '297mm' : '210mm'
      printRef.current.style.minHeight = isLandscape ? '210mm' : '297mm'

      if (containerRef.current) {
        containerRef.current.scrollTop = 0
      }
    }
  }, [options.orientation, options.pageSize])

  const handlePrint = useReactToPrint({
    documentTitle: title,
    onAfterPrint: () => {
      // onClose()
    },
    contentRef: printRef,
    pageStyle: `
      @page {
        size: ${options.pageSize} ${options.orientation};
        margin: 0.5cm;
      }
      @media print {
        html, body {
          margin: 0;
          padding: 0;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `,
  })

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperComponent={StyledDialogPaper}
    >
      <PrintToolbar
        options={options}
        onClose={onClose}
        onPrint={handlePrint}
        setOptions={setOptions}
        title={title}
      />

      <StyledPreviewContainer ref={containerRef}>
        <StyledPrintContent
          ref={printRef}
          className="print-content"
          orientation={options.orientation}
          elevation={3}
        >
          {children}
        </StyledPrintContent>
      </StyledPreviewContainer>

      <style
        dangerouslySetInnerHTML={{
          __html: `
          @media print {
            body * {
              visibility: hidden;
              margin: 0;
              padding: 0;
            }
            
            .MuiDialog-root {
              position: absolute !important;
              left: 0;
              top: 0;
              height: 100%;
              width: 100%;
              z-index: 9999;
              margin: 0 !important;
              padding: 0 !important;
            }
            
            .MuiDialog-paper {
              box-shadow: none !important;
              width: 100% !important;
              height: 100% !important;
              max-width: none !important;
              max-height: none !important;
              overflow: visible !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            
            .print-content, .print-content * {
              visibility: visible;
            }
            
            .print-content {
              position: absolute;
              left: 0;
              top: 0;
              margin: 0 !important;
              box-shadow: none !important;
              padding: ${options.pageSize === 'a4' ? '1cm' : '0.5in'} !important;
              width: 100% !important;
              min-height: auto !important;
              height: auto !important;
            }
          }
        `,
        }}
      />
    </Dialog>
  )
}

export default PrintModal
